// const API_URL = 'http://localhost:4000/api';
// const API_URL = '/api';
// const API_URL = 'http://localhost:4027/api';
// const API_URL = 'https://bitacoreba.cl/api';

const API_URL = 'https://bitacoreba.cl/api';

export default async function api(
  path,
  { method = 'GET', body, token, suppressLogoutOn401 = false } = {}
) {
  const res = await fetch(API_URL + path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });

  if (!res.ok) {
    // Mensaje útil aunque la respuesta no sea JSON
    let msg = `HTTP ${res.status}`;
    try {
      const data = await res.clone().json();
      msg = data?.error || msg;
    } catch {
      try {
        const text = await res.text();
        msg = text || msg;
      } catch {/* noop */ }
    }

    // 401: logout global (salvo login)
    if ((res.status === 401 || /invalid token/i.test(msg)) && !suppressLogoutOn401) {
      window.dispatchEvent(new CustomEvent('auth:logout'));
      const err = new Error('SESSION_EXPIRED');
      err.code = 'SESSION_EXPIRED';
      throw err;
    }
    throw new Error(msg);
  }

  const payload = await res.json().catch(() => ({}));
  return payload;
}

/* ===== Descarga (blob) ===== */
function buildQS(obj = {}) {
  const qs = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  return qs.toString();
}

export async function apiBlob(path, { token, query } = {}) {
  const qs = query ? `?${buildQS(query)}` : '';
  const res = await fetch(API_URL + path + qs, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
  if (!res.ok) {
    const msg = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return res.blob();
}

/* ===== Auth ===== */
export const loginApi = (email, password) =>
  api('/auth/login', {
    method: 'POST',
    body: { email, password },
    suppressLogoutOn401: true
  });

/* ===== Users ===== */
export const listUsersApi = (token) => api('/users', { token });
export const updateUserApi = (id, data, token) =>
  api(`/users/${id}`, { method: 'PATCH', body: data, token });
export const createUserApi = (data, token) =>
  api('/users', { method: 'POST', body: data, token });
export const setUserStatusApi = (id, s, token) =>
  api(`/users/${id}/status`, { method: 'PATCH', body: { status: s }, token });
export const setUserPasswordApi = (id, pw, token) =>
  api(`/users/${id}/password`, { method: 'PATCH', body: { password: pw }, token });
export const sendWelcomeEmailApi = (userId, password, token) =>
  api(`/users/${userId}/send-welcome-email`, { method: 'POST', body: { password }, token });

/* ===== Clients ===== */
export const listClientsApi = (token) => api('/clients', { token });
export const createClientApi = (data, token) =>
  api('/clients', { method: 'POST', body: data, token });
export const updateClientApi = (id, data, token) =>
  api(`/clients/${id}`, { method: 'PATCH', body: data, token });
export const setClientStatusApi = (id, s, token) =>
  api(`/clients/${id}/status`, { method: 'PATCH', body: { status: s }, token });

/* ===== Projects ===== */
export const listProjectsApi = (token, { mine = false } = {}) =>
  api(`/projects${mine ? '?mine=1' : ''}`, { token });
export const createProjectApi = (data, token) =>
  api('/projects', { method: 'POST', body: data, token });
export const updateProjectApi = (id, data, token) =>
  api(`/projects/${id}`, { method: 'PATCH', body: data, token });
export const setProjectStatusApi = (id, status, token) =>
  api(`/projects/${id}/status`, { method: 'PATCH', body: { status }, token });

/* ===== Project members ===== */
export const listProjectMembersApi = (projectId, token) =>
  api(`/projects/${projectId}/members`, { token });
export const addProjectMemberApi = (projectId, data, token) =>
  api(`/projects/${projectId}/members`, { method: 'POST', body: data, token });
export const updateProjectMemberApi = (pmId, data, token) =>
  api(`/projects/members/${pmId}`, { method: 'PATCH', body: data, token });

/* ===== Project work-logs ===== */
export const listProjectLogsApi = (projectId, token, limit = 5) =>
  api(`/projects/${projectId}/work-logs?limit=${limit}`, { token });

export const createProjectLogApi = (projectId, data, token) =>
  api(`/projects/${projectId}/work-logs`, { method: 'POST', body: data, token });

export const updateProjectLogApi = (projectId, logId, data, token) =>
  api(`/projects/${projectId}/work-logs/${logId}`, {
    method: 'PATCH',
    body: data,
    token
  });

/* ===== Export ===== */
export const exportWorklogsApi = (token, query) =>
  apiBlob('/work-logs/export.xlsx', { token, query });

export const exportProjectWorklogsApi = (projectId, token, query) =>
  apiBlob(`/projects/${projectId}/work-logs/export.xlsx`, { token, query });

export const listWorklogsApi = (token, query = {}) => {
  const qs = new URLSearchParams(query).toString();
  return api(`/work-logs?${qs}`, { token });
};

export const approveWorklogsApi = (logIds, approved, token) =>
  api('/work-logs/approve', {
    method: 'PATCH',
    body: { log_ids: logIds, approved },
    token
  });

/* ===== Compat: DELETE work-log ===== */
export async function deleteProjectLogApiCompat(projectId, logId, token) {
  const attempts = [
    { m: 'DELETE', p: `/projects/${projectId}/work-logs/${logId}` },
    { m: 'DELETE', p: `/work-logs/${logId}` }
  ];
  let lastErr;
  for (const a of attempts) {
    try {
      return await api(a.p, { method: a.m, token });
    } catch (e) {
      lastErr = e;
      const msg = String(e?.message || '').toLowerCase();
      if (/401|session_expired/.test(msg)) throw e;
      continue;
    }
  }
  throw lastErr || new Error('No delete route matched');
}
export async function updateProjectLogApiCompat(projectId, logId, data, token) {
  const attempts = [
    { m: 'PATCH', p: `/projects/${projectId}/work-logs/${logId}`, body: data },
    { m: 'PATCH', p: `/work-logs/${logId}`, body: data },
    { m: 'PUT', p: `/work-logs/${logId}`, body: data },
    { m: 'PUT', p: `/projects/${projectId}/work-logs/${logId}`, body: data },
    { m: 'PATCH', p: `/work-logs`, body: { id: Number(logId), ...data } },
    { m: 'PUT', p: `/work-logs`, body: { id: Number(logId), ...data } },
    { m: 'PATCH', p: `/projects/${projectId}/work-logs`, body: { id: Number(logId), ...data } },
    { m: 'PUT', p: `/projects/${projectId}/work-logs`, body: { id: Number(logId), ...data } }
  ];

  for (const a of attempts) {
    try {
      return await api(a.p, { method: a.m, body: a.body, token });
    } catch (e) {
      const msg = String(e?.message || '').toLowerCase();
      if (/401|session_expired/.test(msg)) throw e;
      continue;
    }
  }

  throw new Error('NO_UPDATE_ENDPOINT');
}

/* ===== Work-log Drafts ===== */
export const listProjectDraftsApi = (projectId, token) =>
  api(`/projects/${projectId}/work-log-drafts`, { token });

export const upsertDraftApi = (projectId, data, token) => {
  if (data?.id) {
    // Actualizar borrador existente
    return api(`/projects/${projectId}/work-log-drafts/${data.id}`, {
      method: 'PATCH',
      body: data,
      token
    });
  } else {
    // Crear nuevo borrador
    return api(`/projects/${projectId}/work-log-drafts`, {
      method: 'POST',
      body: data,
      token
    });
  }
};

export const publishDraftApi = (draftId, token) =>
  api(`/work-log-drafts/${draftId}/publish`, {
    method: 'POST',
    token
  });

export const deleteDraftApi = (draftId, token) =>
  api(`/work-log-drafts/${draftId}`, {
    method: 'DELETE',
    token
  });

export const markLogsAsInvoicedApi = (logIds, token) =>
  api('/work-logs/mark-invoiced', {
    method: 'PATCH',
    body: { log_ids: logIds, billable: 1 },
    token
  });